import React from 'react';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { View, StyleSheet, TouchableOpacity, Animated, Text } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';

import HomeScreen from '../users/HomeScreenUser';
import DocumentsScreen from '../users/HistoriqueScreenUser';
import CentreScreen from '../users/CentreScreenUser';
import ProfileScreen from '../users/ProfileScreenUser';

const Tab = createBottomTabNavigator();

const CustomTabBar = ({ state, descriptors, navigation }) => {
  const animatedValues = React.useRef(
    state.routes.map(() => new Animated.Value(0))
  ).current;

  // Animation pour le label
  const labelAnimatedValues = React.useRef(
    state.routes.map(() => new Animated.Value(0))
  ).current;

  React.useEffect(() => {
    // Animation du label quand l'onglet est actif
    labelAnimatedValues.forEach((animatedValue, index) => {
      Animated.timing(animatedValue, {
        toValue: state.index === index ? 1 : 0,
        duration: 300,
        useNativeDriver: true,
      }).start();
    });
  }, [state.index]);

  return (
    <View style={styles.container}>
      {state.routes.map((route, index) => {
        const { options } = descriptors[route.key];
        const isFocused = state.index === index;

        const onPress = () => {
          const event = navigation.emit({
            type: 'tabPress',
            target: route.key,
          });

          if (!isFocused && !event.defaultPrevented) {
            navigation.navigate(route.name);
          }

          // Animation de pression
          Animated.sequence([
            Animated.timing(animatedValues[index], {
              toValue: 1,
              duration: 150,
              useNativeDriver: true,
            }),
            Animated.timing(animatedValues[index], {
              toValue: 0,
              duration: 150,
              useNativeDriver: true,
            }),
          ]).start();
        };

        const scale = animatedValues[index].interpolate({
          inputRange: [0, 0.5, 1],
          outputRange: [1, 1.15, 1],
        });

        const labelOpacity = labelAnimatedValues[index].interpolate({
          inputRange: [0, 1],
          outputRange: [0.6, 1],
        });

        const labelScale = labelAnimatedValues[index].interpolate({
          inputRange: [0, 1],
          outputRange: [0.85, 1],
        });

        // Configuration des icônes et labels
        let iconName, label;
        if (route.name === 'Accueil') {
          iconName = 'home';
          label = 'Accueil';
        } else if (route.name === 'Documents') {
          iconName = 'file-document-multiple';
          label = 'Mes documents';
        } else if (route.name === 'Centres') {
          iconName = 'school';
          label = 'Centre';
        } else if (route.name === 'Profil') {
          iconName = 'account';
          label = 'Profile';
        }

        return (
          <TouchableOpacity
            key={index}
            onPress={onPress}
            style={styles.tabButton}
            activeOpacity={0.8}
          >
            <Animated.View
              style={[
                styles.tabContent,
                { transform: [{ scale }] },
              ]}
            >
              <View
                style={[
                  styles.iconContainer,
                  isFocused && styles.activeIconContainer,
                ]}
              >
                <MaterialCommunityIcons
                  name={iconName}
                  size={isFocused ? 26 : 24}
                  color={isFocused ? '#165ca2' : '#fff'}
                />
                {isFocused && <View style={styles.dot} />}
              </View>
              
              <Animated.Text
                style={[
                  styles.label,
                  isFocused ? styles.activeLabel : styles.inactiveLabel,
                  {
                    opacity: labelOpacity,
                    transform: [{ scale: labelScale }],
                  },
                ]}
                numberOfLines={1}
                adjustsFontSizeToFit
              >
                {label}
              </Animated.Text>
            </Animated.View>
          </TouchableOpacity>
        );
      })}
    </View>
  );
};

export default function Navigation() {
  return (
    <Tab.Navigator
      tabBar={(props) => <CustomTabBar {...props} />}
      screenOptions={{
        headerShown: false, // Cette ligne cache le header
      }}
    >
      <Tab.Screen 
        name="Accueil" 
        component={HomeScreen}
      />
      <Tab.Screen 
        name="Documents" 
        component={DocumentsScreen}
      />
      <Tab.Screen 
        name="Centres" 
        component={CentreScreen}
      />
      <Tab.Screen 
        name="Profil" 
        component={ProfileScreen}
      />
    </Tab.Navigator>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    position: 'absolute',
    bottom: 10,
    backgroundColor: '#165ca2',
    borderRadius: 25,
    marginHorizontal: 10,
    paddingVertical: 12,
    paddingHorizontal: 5,
    elevation: 12,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 6,
    },
    shadowOpacity: 0.2,
    shadowRadius: 12,
  },
  tabButton: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 5,
    paddingHorizontal: 10,
  },
  tabContent: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconContainer: {
    padding: 8,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 40,
    minWidth: 40,
  },
  activeIconContainer: {
    backgroundColor: '#F2F0FC',
  },
  dot: {
    position: 'absolute',
    bottom: -2,
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: '#165ca2',
  },
  label: {
    fontSize: 11,
    fontWeight: '600',
    textAlign: 'center',
    marginTop: 4,
    minHeight: 14,
  },
  activeLabel: {
    color: '#F2F0FC',
    fontWeight: '700',
  },
  inactiveLabel: {
    color: '#B8D4F0',
    fontWeight: '500',
  },
});